<script setup lang="ts">
import { computed, onBeforeMount, Ref, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import FileDTO = App.Data.FileDTO;
import StoredDocumentDO = App.Data.StoredDocumentDO;
import SelectStoredDocumentDialog from "@/modules/dashboards/components/SelectStoredDocumentDialog.vue";
import { useDragAndDrop } from "@/ui/composables/useDragAndDrop";
import BookmarkDO = App.Data.BookmarkDO;
import { required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import IconPicker from "@/ui/components/IconPicker.vue";
import { toastApiResult } from "@/toaster";
import useToast from "@/ui/composables/useToast";

const { t } = useI18n();

const emit = defineEmits(["update-bookmarks"]);

const props = defineProps<{
	bookmarks: BookmarkDO[];
}>();

const isDialogVisible = defineModel<boolean>();

const defaultIcon = "fa-light fa-bookmark";

const localBookmarks: Ref<BookmarkDO[]> = ref();
const selectedStoredDocument: Ref<StoredDocumentDO> = ref();
const selectedIcon = ref<string>(defaultIcon);
const isFormValid = ref(false);
const storedDocuments = ref<StoredDocumentDO[]>([]);
const isSelectStoredDocumentDialogVisible = ref(false);
const isLoading = ref(true);
const isChanged = ref(false);

const maxNoOfBookmarks = 10;

const { dragstartHandle, dragoverHandle, dragleaveHandle, dragdropHandle } =
	useDragAndDrop(localBookmarks);

async function fetchStoredDocuments() {
	const response = await useApiClient(
		"documents"
	).getAllStoredDocumentsForUser();
	if (response.isOk()) {
		storedDocuments.value = response.value;
		isLoading.value = false;
	}
}

const displayTitle = computed({
	get() {
		return selectedStoredDocument.value?.title ?? "";
	},
	set() {},
});

const selectStoredDocument = (file: FileDTO) => {
	selectedStoredDocument.value = file as StoredDocumentDO;
};

const showSelectStoredDocument = (value) => {
	if (value) {
		isSelectStoredDocumentDialogVisible.value = true;
	}
};

const addBookmark = () => {
	if (localBookmarks.value.find((bm) => bm.icon === selectedIcon.value)) {
		useToast().error("This icon is already used in another bookmark.", {
			timeout: 5000,
		});
		return;
	} else if (
		localBookmarks.value.find(
			(bm) => bm.stored_document.id === selectedStoredDocument.value.id
		)
	) {
		useToast().error("This document is already used in another bookmark.", {
			timeout: 5000,
		});
		return;
	}

	const newBookmark: BookmarkDO = {
		id: null,
		icon: selectedIcon.value,
		stored_document: selectedStoredDocument.value,
		sort_order: null,
	};
	localBookmarks.value.push(newBookmark);

	selectedIcon.value = defaultIcon;
	selectedStoredDocument.value = undefined;
};

onBeforeMount(async () => {
	localBookmarks.value = [...props.bookmarks];
	await fetchStoredDocuments();
});

const removeBookmark = (index) => {
	if (index >= 0 && index < localBookmarks.value.length) {
		localBookmarks.value.splice(index, 1);
	}
};

const saveBookmarks = async () => {
	const result = await toastApiResult(
		useApiClient("explorer").saveBookmarks(localBookmarks.value)
	);
	if (result.isOk()) {
		isDialogVisible.value = false;
		emit("update-bookmarks");
	}
};

watch(
	localBookmarks,
	(newValue, oldValue) => {
		if (!oldValue) return;
		isChanged.value = true;
	},
	{ deep: true }
);
</script>

<template>
	<select-stored-document-dialog
		v-if="isSelectStoredDocumentDialogVisible"
		v-model="isSelectStoredDocumentDialogVisible"
		:fetch-documents="fetchStoredDocuments"
		:allow-create-new="false"
		@file-selected="selectStoredDocument"
	/>

	<v-dialog
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="600"
	>
		<v-card>
			<v-toolbar>
				<v-toolbar-title>
					<i class="fal fa-bookmark fa-lg mr-3"></i>
					Add bookmark to toolbar
				</v-toolbar-title>

				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />
			<div class="px-5">
				<div class="pt-5">
					<div
						class="node"
						draggable="true"
						v-for="(item, index) in localBookmarks"
						@dragstart="(ev) => dragstartHandle(ev, index)"
						:key="index"
					>
						<i class="drag-handle fa-regular fa-grip-dots-vertical"></i>
						<div class="item-label d-flex">
							<div class="mr-5" :key="`${index}-${item.icon}`">
								<v-icon size="22">{{ item.icon }}</v-icon>
							</div>
							<div>
								<span class="text-caption text-medium-emphasis"
									>Opens configuration:</span
								>
								<v-chip density="compact" variant="flat" class="ml-2">
									{{ item.stored_document.title }}
								</v-chip>
							</div>
						</div>
						<span
							class="order-dropzone"
							@dragover="(ev) => dragoverHandle(ev, index)"
							@dragleave="(ev) => dragleaveHandle(ev)"
							@drop="(ev) => dragdropHandle(ev, index)"
						>
						</span>
						<button
							class="drag-handle delete pa-0"
							@click="removeBookmark(index)"
						>
							<i class="fa-solid fa-xmark"></i>
						</button>
					</div>
				</div>

				<template v-if="localBookmarks.length < maxNoOfBookmarks">
					<v-form v-model="isFormValid">
						<div class="d-flex mt-5 mb-2 ga-2 align-center">
							<div class="text-caption text-emphasis">New bookmark</div>

							<div class="text-caption text-medium-emphasis">Icon:</div>
							<icon-picker v-model="selectedIcon" />
							<div class="text-caption text-medium-emphasis">
								Configuration:
							</div>
							<v-text-field
								density="compact"
								@update:focused="showSelectStoredDocument"
								v-model="displayTitle"
								hide-details
								variant="filled"
								placeholder="Select a configuration"
								:rules="[required(t)]"
							/>
							<v-btn
								variant="tonal"
								color="primary"
								:disabled="isFormValid === false"
								@click="addBookmark()"
							>
								Add
							</v-btn>
						</div>
					</v-form>
				</template>
				<template v-else>
					<v-alert class="mt-5" variant="tonal" border="start" color="info">
						You can add a maximum of {{ maxNoOfBookmarks }} bookmarks. Please
						remove one to add a new one.
					</v-alert>
				</template>
			</div>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn
					class="mt-1"
					variant="flat"
					color="primary"
					@click="saveBookmarks"
					:disabled="!isChanged"
				>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.node {
	position: relative;
	display: flex;
	align-items: center;
	height: 3rem;
	padding: 0.5rem;
	color: #333;
	background: rgb(var(--color-background));
	border: 1px solid rgb(var(--color-secondary));
	user-select: none;
	cursor: grab;
	transition: all 125ms ease;

	&:not(:first-child) {
		border-top: none;
	}

	&:first-child {
		border-top-right-radius: 8px;
		border-top-left-radius: 8px;
	}

	&:last-child {
		border-bottom-right-radius: 8px;
		border-bottom-left-radius: 8px;
	}
}

.drag-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	padding-right: 0.75rem;
	cursor: grab;
	color: #888;
	height: 1.25em;

	&.delete {
		cursor: pointer;
		margin: 0 5px;
	}

	&:hover {
		color: #555;
	}
}

.item-label {
	flex-grow: 1;
	font-size: 1rem;
	color: #333;
	font-weight: 500;
	display: flex;
	line-height: normal;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	& .description {
		font-size: 0.75rem;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}
}

.order-dropzone {
	position: absolute;
	bottom: -0.5rem;
	left: 0;
	width: 100%;
	height: 1rem;
	z-index: 100;
	border: 1px dashed transparent;
	transition: all 125ms ease;

	&.over {
		border-color: #ff9800;
		background: rgba(255, 152, 0, 0.1);
	}
}
</style>
